import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, Video, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import api from '../../lib/api';

const VideoUploader = ({ 
  lessonId = null,
  onUploadSuccess,
  onUploadError,
  maxSizeMB = 250,
  className = ""
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const fileInputRef = useRef(null);

  const allowedExtensions = ['.mp4', '.webm', '.avi', '.mov', '.mkv'];
  const allowedMimeTypes = ['video/mp4', 'video/webm', 'video/x-msvideo', 'video/quicktime', 'video/x-matroska'];

  const validateFile = useCallback((selectedFile) => {
    // Check file extension
    const fileName = selectedFile.name.toLowerCase();
    const hasValidExt = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExt) {
      return t('video.invalidType', { extensions: allowedExtensions.join(', ') }) || 
             `Type de fichier non autorisé. Extensions permises: ${allowedExtensions.join(', ')}`;
    }

    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (selectedFile.size > maxSizeBytes) {
      const fileSizeMB = (selectedFile.size / (1024 * 1024)).toFixed(1);
      return t('video.fileTooLarge', { size: fileSizeMB, max: maxSizeMB }) ||
             `Fichier trop volumineux (${fileSizeMB} MB). Maximum: ${maxSizeMB} MB`;
    }

    return null;
  }, [maxSizeMB, t]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }

    setFile(selectedFile);
    setError('');
    setUploadedVideo(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;

    const validationError = validateFile(droppedFile);
    if (validationError) {
      setError(validationError);
      return;
    }

    setFile(droppedFile);
    setError('');
    setUploadedVideo(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB — passe sous les limites de proxy

  const pollUploadStatus = async (uploadId) => {
    for (let attempt = 0; attempt < 300; attempt++) {
      await new Promise(r => setTimeout(r, 2000));
      const res = await api.get(`/videos/upload-status/${uploadId}`);
      if (res.data.status === 'done') return res.data.result;
      if (res.data.status === 'error') throw new Error(res.data.error || 'Échec du stockage');
    }
    throw new Error('Délai de traitement dépassé');
  };

  const uploadVideo = async () => {
    if (!file) return;

    setUploading(true);
    setProgress(0);
    setError('');

    try {
      const uploadId = (window.crypto?.randomUUID)
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      let response = null;

      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const formData = new FormData();
        formData.append('file', blob, file.name);
        formData.append('upload_id', uploadId);
        formData.append('chunk_index', i);
        formData.append('total_chunks', totalChunks);
        formData.append('filename', file.name);
        formData.append('kind', 'video');
        formData.append('directory', 'videos');
        if (lessonId) formData.append('lesson_id', lessonId);

        response = await api.post('/videos/upload-chunk', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      let resultData = response.data;
      if (resultData.processing) {
        setFinalizing(true);
        resultData = await pollUploadStatus(uploadId);
        setFinalizing(false);
      }
      if (!resultData?.cdn_url) {
        throw new Error('Le stockage n\'a pas confirmé la vidéo — réessayez');
      }

      const fileName = file.name;
      setUploadedVideo({ ...resultData, original_filename: fileName });
      setFile(null);
      
      if (onUploadSuccess) {
        onUploadSuccess(resultData);
      }
    } catch (err) {
      setFinalizing(false);
      const errorMessage = err.response?.data?.detail || err.message ||
                          t('video.uploadFailed') || 
                          'Échec de l\'upload';
      setError(errorMessage);
      
      if (onUploadError) {
        onUploadError(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setError('');
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Drop Zone */}
      {!file && !uploadedVideo && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20 transition-colors"
          data-testid="video-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedMimeTypes.join(',')}
            onChange={handleFileSelect}
            className="hidden"
            data-testid="video-file-input"
          />
          
          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('video.dropzone.title') || 'Glissez-déposez votre vidéo ici'}
          </p>
          
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('video.dropzone.subtitle') || 'ou cliquez pour sélectionner un fichier'}
          </p>
          
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {t('video.dropzone.formats', { formats: 'MP4, WebM, AVI, MOV, MKV', max: maxSizeMB }) ||
             `Formats: MP4, WebM, AVI, MOV, MKV • Maximum: ${maxSizeMB} MB`}
          </p>
        </div>
      )}

      {/* Selected File */}
      {file && !uploading && !uploadedVideo && (
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <Video className="w-8 h-8 text-emerald-600" />
            <div>
              <p className="font-medium text-gray-800 dark:text-gray-200 truncate max-w-xs">
                {file.name}
              </p>
              <p className="text-sm text-gray-500">
                {formatFileSize(file.size)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              onClick={clearFile}
              variant="ghost"
              size="sm"
              className="text-gray-500 hover:text-red-500"
              data-testid="video-clear-btn"
            >
              <X className="w-4 h-4" />
            </Button>
            
            <Button
              onClick={uploadVideo}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="video-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('video.uploadButton') || 'Téléverser'}
            </Button>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center space-x-3 mb-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <span className="font-medium text-blue-700 dark:text-blue-300">
              {finalizing
                ? 'Enregistrement sur le stockage...'
                : (t('video.uploading') || 'Téléversement en cours...')}
            </span>
            <span className="text-sm text-blue-600">{finalizing ? '' : `${progress}%`}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      )}

      {/* Upload Success */}
      {uploadedVideo && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center space-x-3 min-w-0">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-green-700 dark:text-green-300">
                {t('video.uploadSuccess') || 'Vidéo téléversée avec succès!'}
              </p>
              <p className="text-sm text-green-600 dark:text-green-400 truncate">
                {uploadedVideo.original_filename || (uploadedVideo.cdn_url || '').split('/').pop()}
              </p>
            </div>
            <Button
              onClick={() => {
                setUploadedVideo(null);
                clearFile();
              }}
              variant="outline"
              size="sm"
              className="border-green-300 text-green-700 hover:bg-green-100"
              data-testid="video-upload-another-btn"
            >
              {t('video.uploadAnother') || 'Ajouter une autre'}
            </Button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-600 dark:text-red-400" data-testid="video-error">
            {error}
          </p>
        </div>
      )}
    </div>
  );
};

export default VideoUploader;
